import React, { useState, useEffect, useCallback } from 'react';
import { Container, Nav, Navbar, Button, OverlayTrigger, Tooltip, Dropdown } from 'react-bootstrap';
import { Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TopicIcon from '@mui/icons-material/Topic';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImageIcon from '@mui/icons-material/Image';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Auth, Subscribers, Campaigns, Tags, Topics, Reports, Documentation, Users, Analytics, Templates, Settings, Dashboard, Media } from './pages';
import { DebounceJobs } from './pages/Dashboard';
import FileChanges from './pages/Settings/FileChanges';
import { isConfigured } from './services/config';

const AnalyticsWrapper = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  return campaignId ? <Analytics campaignId={parseInt(campaignId)} /> : null;
};

const DashboardContent = () => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState ? JSON.parse(savedState) : false;
  });
  const { user, logout } = useAuth();
  const [configComplete, setConfigComplete] = useState<boolean | null>(null);
  const [hostname] = useState(window.location.hostname);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    isConfigured().then(setConfigComplete);
  }, []);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = () => {
    logout();
  };

  const handleOpenAnalytics = useCallback((event: any) => {
    navigate(`/analytics/${event.detail.campaignId}`);
  }, [navigate]);

  useEffect(() => {
    window.addEventListener('openAnalytics', handleOpenAnalytics);
    return () => window.removeEventListener('openAnalytics', handleOpenAnalytics);
  }, [handleOpenAnalytics]);

  const renderTooltip = (message: string) => (
    <Tooltip id="button-tooltip">{message}</Tooltip>
  );

  // Helper function to check if a route is accessible based on user role
  const isRouteAccessible = (path: string): boolean => {
    // Admin users have access to all routes
    if (user?.isAdmin) {
      return true;
    }

    // Non-admin users: use switch case for role-based access control
    switch (path) {
      case '/':
      case '/reports':
        // Non-admin users can access Dashboard and Reports
        return true;
      
      // Add more accessible routes for non-admin users here in future
      // case '/new-route':
      //   return true;
      
      default:
        // All other routes are restricted for non-admin users
        return false;
    }
  };

  const NavItemLink = ({ icon: Icon, text, path, adminOnly = false }: { 
    icon: React.ElementType, 
    text: string, 
    path: string,
    adminOnly?: boolean
  }) => {
    // Check if route is accessible for current user role
    if (!isRouteAccessible(path)) {
      return null;
    }

    const isDisabled = configComplete === false && path !== '/settings';
    const isActive = location.pathname === path;
    
    const linkContent = (
      <Nav.Link 
        as={Link}
        to={isDisabled ? '#' : path}
        className={`text-white ${isActive ? 'active-nav-link' : ''} ${isDisabled ? 'disabled opacity-50' : ''}`}
        style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}
      >
        <Icon className="me-2" />
        {!isCollapsed && <span>{text}</span>}
      </Nav.Link>
    );

    if (isCollapsed) {
      return (
        <OverlayTrigger
          placement="right"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip(isDisabled ? 'Complete setup first' : text)}
        >
          {linkContent}
        </OverlayTrigger>
      );
    } else {
      return linkContent;
    }
  };

  return (
    <div className="App d-flex">
      <Navbar bg="primary" variant="dark" expand="lg" className="w-100 fixed-top">
        <Container fluid>
          <Button variant="outline-light" onClick={toggleSidebar} className="me-2 border-0">
            <MenuIcon />
          </Button>
          <Navbar.Brand href="#"><b>[{hostname}]</b> - Mailing dashboard</Navbar.Brand>
          
          <Navbar.Collapse className="justify-content-end">
            <Dropdown>
              <Dropdown.Toggle variant="outline-light" id="user-dropdown" className="border-0">
                <PersonIcon className="me-1" />
                {user?.username}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.ItemText>
                  <small className="text-muted">
                    {user?.isAdmin ? 'Administrator' : 'User'}
                  </small>
                </Dropdown.ItemText>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleLogout}>
                  <LogoutIcon className="me-2" fontSize="small" />
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div className="d-flex flex-grow-1">
        <div id="sidebar-wrapper" className={isCollapsed ? 'collapsed' : ''}>
          <Nav className="flex-column pt-5">
            <NavItemLink icon={DashboardIcon} text="Dashboard" path="/" />
            <NavItemLink icon={TopicIcon} text="Topics" path="/topics" />
            <NavItemLink icon={EmailIcon} text="Templates" path="/email-templates" />
            <NavItemLink icon={LocalOfferIcon} text="Tags" path="/tags" />
            <NavItemLink icon={PeopleIcon} text="Subscribers" path="/subscribers" />
            <NavItemLink icon={MailOutlineIcon} text="Campaigns" path="/campaigns" />
            <NavItemLink icon={BarChartIcon} text="Reports" path="/reports" />
            <NavItemLink icon={CheckCircleIcon} text="Debounce Jobs" path="/debounce-jobs" />
            <NavItemLink icon={PersonIcon} text="Users" path="/users" adminOnly={true} />
            <NavItemLink icon={FolderIcon} text="Mail Server Config" path="/file-changes" adminOnly={true} />
            <NavItemLink icon={DescriptionIcon} text="API Docs" path="/api-docs" />
            <NavItemLink icon={ImageIcon} text="Media" path="/media" />
            <NavItemLink icon={SettingsIcon} text="Settings" path="/settings" adminOnly={true} />
          </Nav>
        </div>

        <div id="page-content-wrapper" className={isCollapsed ? 'collapsed-content' : ''}>
          <Routes>
            {/* Routes accessible based on user role */}
            <Route path="/" element={<Dashboard />} />
            <Route path="/reports" element={<Reports />} />
            
            {/* Admin-only routes */}
            {user?.isAdmin && (
              <>
                <Route path="/subscribers" element={<Subscribers />} />
                <Route path="/campaigns" element={<Campaigns />} />
                <Route path="/email-templates" element={<Templates />} />
                <Route path="/analytics/:campaignId" element={<AnalyticsWrapper />} />
                <Route path="/topics" element={<Topics />} />
                <Route path="/tags" element={<Tags />} />
                <Route path="/debounce-jobs" element={<DebounceJobs />} />
                <Route path="/users" element={<Users />} />
                <Route path="/file-changes" element={<FileChanges />} />
                <Route path="/api-docs" element={<Documentation />} />
                <Route path="/media" element={<Media />} />
                <Route path="/settings" element={<Settings showOnlyRequired={configComplete === false} onConfigComplete={() => setConfigComplete(true)} />} />
              </>
            )}
          </Routes>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

const AppContent = () => {
  const { isAuthenticated, isLoading, login } = useAuth();

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return <Auth onLogin={login} />;
  }

  return (
    <ProtectedRoute>
      <DashboardContent />
    </ProtectedRoute>
  );
};

export default App;